import { ADEQUATION, PALIERS, estDisponible } from '../data/postes.js';

/**
 * À quel point ce joueur convient à ce slot, de 0 à 2.
 *
 * `alt` est de l'OBSERVATION — « il y a réellement joué » — et bat toujours la
 * déduction : un joueur qui a déjà tenu le poste passe devant celui que la matrice
 * juge théoriquement proche. C'est la règle qui fait sortir Sy en tête des pistons
 * quand Still passe à trois derrière, alors que sa fiche dit « latéral droit ».
 */
export function score(joueur, slot) {
  if (!joueur || !slot) return 0;
  if (joueur.poste === slot) return 2.0;
  // 1,9 et non 1,8 : la meilleure valeur DÉDUITE de la matrice vaut justement 1,8
  // (un latéral droit vers un piston droit, par exemple). À égalité, l'observation
  // n'apportait rien — un joueur qu'on a vu tenir le poste sortait au même rang que
  // celui dont on le suppose seulement capable, ce qui vide `alt` de son sens.
  if (joueur.alt?.includes(slot)) return 1.9;

  let s = ADEQUATION[joueur.poste]?.[slot] ?? 0;
  // Transitivité amortie : passer par un poste secondaire coûte 15 %. Sans
  // l'amortissement, un joueur à trois postes secondaires finirait mieux noté partout
  // qu'un spécialiste, ce qui est le contraire de ce qu'on veut lire.
  for (const a of joueur.alt ?? []) {
    s = Math.max(s, 0.85 * (ADEQUATION[a]?.[slot] ?? 0));
  }
  return s;
}

/**
 * Les candidats pour un slot, groupés par palier.
 *
 * Les indisponibles sortent de la liste, ils ne sont pas grisés. `dejaPlaces` écarte
 * ceux qui sont déjà sur le terrain : proposer un joueur qu'il faudrait d'abord
 * retirer d'ailleurs est une impasse déguisée en suggestion.
 */
export function candidats(effectif, slot, { dejaPlaces = [], recherche = '' } = {}) {
  const q = recherche.trim().toLowerCase();
  const places = new Set(dejaPlaces.filter(Boolean));

  const notes = effectif
    .filter((j) => !places.has(j.id))
    .filter((j) => (q ? `${j.nom} ${j.numero ?? ''}`.toLowerCase().includes(q) : true))
    .map((j) => ({ joueur: j, valeur: score(j, slot), dispo: estDisponible(j) }));

  // Une recherche explicite affiche TOUT ce qui correspond, y compris les blessés et
  // les scores nuls : si on tape un nom, c'est qu'on veut ce joueur-là, et l'app n'a
  // pas à décider qu'il n'a rien à faire ici.
  if (q) {
    return [{
      titre: 'Résultats', replie: false,
      joueurs: notes.sort((a, b) => b.valeur - a.valeur || a.joueur.nom.localeCompare(b.joueur.nom)),
    }];
  }

  const groupes = PALIERS.map((p) => ({ titre: p.titre, replie: p.replie, joueurs: [] }));
  const reste = [];
  for (const n of notes) {
    if (!n.dispo) continue;
    const i = PALIERS.findIndex((p) => n.valeur >= p.min);
    if (i < 0) reste.push(n); else groupes[i].joueurs.push(n);
  }
  for (const g of groupes) g.joueurs.sort((a, b) => b.valeur - a.valeur || a.joueur.nom.localeCompare(b.joueur.nom));
  if (reste.length) {
    reste.sort((a, b) => b.valeur - a.valeur || a.joueur.nom.localeCompare(b.joueur.nom));
    groupes.push({ titre: "Tout l'effectif", replie: true, joueurs: reste });
  }
  return groupes.filter((g) => g.joueurs.length);
}
